import React, { useState, useRef, useEffect } from "react";

/* ─── Plain-English sentence builder ───────────────────────────────────────
   Maps a rule's conditions_tree + actions → one readable sentence.
   Falls back gracefully to raw keys when registry data is missing.        */

const OP_LABELS = {
  ">=": "is at least",
  ">":  "is more than",
  "<=": "is at most",
  "<":  "is less than",
  "==": "equals",
  "!=": "is not",
};

function buildRuleSentence(rule, factRegistry, actionRegistry) {
  const children = rule.conditions_tree?.children || [];
  const logic    = rule.conditions_tree?.type || "AND";

  const condParts = children.map(c => {
    const factLabel = factRegistry.find(f => f.fact_key === c.fact)?.label || c.fact;
    const opLabel   = OP_LABELS[c.operator] || c.operator;

    let valueStr;
    if (c.value_type === "DYNAMIC") {
      valueStr = factRegistry.find(f => f.fact_key === c.value_from)?.label || c.value_from || "?";
    } else if (c.value_type === "EXPRESSION") {
      valueStr = c.expression || "?";
    } else {
      valueStr = c.value ?? "";
    }

    return `${factLabel} ${opLabel} ${valueStr}`;
  });

  if (condParts.length === 0) return null;

  const condStr = condParts.join(` ${logic} `);

  const actionLabels = (rule.actions || [])
    .map(a => actionRegistry.find(x => x.action_key === a.action_key)?.label || a.action_key)
    .filter(Boolean);

  return actionLabels.length
    ? `When ${condStr}  →  ${actionLabels.join(", ")}`
    : `When ${condStr}`;
}

const RuleCard = ({
  rule,
  onChange,
  onDelete,
  removing = false,
  factRegistry = [],
  actionRegistry = []
}) => {
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  // Ensure safe defaults
  const conditions = rule.conditions_tree?.children || [];
  const actions = rule.actions || [];

  const toggleActive = () => {
    onChange({ ...rule, is_active: !rule.is_active });
  };

  const updateRuleName = (value) => {
    onChange({ ...rule, rule_name: value });
  };

  const updateConditions = (updatedConditions) => {
    onChange({
      ...rule,
      conditions_tree: {
        type: rule.conditions_tree?.type || "AND",
        children: updatedConditions
      }
    });
  };

  const updateActions = (updatedActions) => {
    onChange({
      ...rule,
      actions: updatedActions
    });
  };

  const sentence = buildRuleSentence(rule, factRegistry, actionRegistry);

  return (
    <div style={cardStyle}>
      {/* HEADER */}
      <div style={headerStyle}>
        <div onClick={() => setExpanded(!expanded)} style={{ flex: 1, cursor: "pointer" }}>
          <div style={ruleTitle}>
            {rule.rule_name || "Unnamed Rule"}
          </div>

          <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span
              style={{
                ...statusBadge,
                background: rule.is_active ? "#28a745" : "#6c757d"
              }}
            >
              {rule.is_active ? "ACTIVE" : "INACTIVE"}
            </span>
            {rule.group_name && (
              <span style={groupChip}>{rule.group_name}</span>
            )}
          </div>

          {/* Plain-English summary — always visible on collapsed card */}
          {sentence && (
            <div style={sentenceBox}>
              <span style={sentenceIcon}>📋</span>
              <span style={sentenceText}>{sentence}</span>
            </div>
          )}
        </div>

        {/* TOGGLE SWITCH */}
        <ToggleSwitch checked={rule.is_active} onChange={toggleActive} />

        {/* KEBAB MENU */}
        {onDelete && (
          <div ref={menuRef} style={{ position: "relative", marginLeft: 10 }}>
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
              style={kebabBtn}
              title="More options"
            >
              ⋮
            </button>
            {menuOpen && (
              <div style={kebabMenu}>
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(); }}
                  disabled={removing}
                  style={kebabMenuItem}
                  onMouseEnter={e => e.currentTarget.style.background = "#fff5f5"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >
                  {removing ? "Removing…" : "🗑 Remove"}
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{ marginLeft: 15, fontSize: 18, cursor: "pointer" }}
             onClick={() => setExpanded(!expanded)}>
          {expanded ? "▲" : "▼"}
        </div>
      </div>

      {/* BODY */}
      {expanded && (
        <div style={{ padding: 20 }}>

          {/* RULE NAME */}
          <SectionTitle title="Rule Configuration" />
          <input
            value={rule.rule_name || ""}
            onChange={(e) => updateRuleName(e.target.value)}
            style={inputStyle}
            placeholder="Enter rule name"
          />

          {/* CONDITION TYPE (AND / OR) */}
          <div style={{ marginBottom: 15 }}>
            <label style={{ fontSize: 13, marginRight: 10 }}>Condition Logic:</label>
            <select
              value={rule.conditions_tree?.type || "AND"}
              onChange={(e) =>
                onChange({
                  ...rule,
                  conditions_tree: {
                    ...rule.conditions_tree,
                    type: e.target.value
                  }
                })
              }
              style={smallInput}
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
            </select>
          </div>

          {/* CONDITIONS */}
          <SectionTitle title="WHEN Conditions" />
          <ConditionBlock
            conditions={conditions}
            factRegistry={factRegistry}
            onChange={updateConditions}
          />

          {/* ACTIONS */}
          <SectionTitle title="THEN Actions" />
          <ActionBlock
            actions={actions}
            actionRegistry={actionRegistry}
            onChange={updateActions}
          />

          {/* SCHEDULING & PRIORITY */}
          <SectionTitle title="Scheduling & Priority" />
          <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>
            Rules in the same <strong>Group</strong> compete — only the top
            <strong> Max Fires</strong> rules (lowest priority number) execute per cycle.
            Rules in different groups can fire simultaneously.
          </div>
          <div style={schedGrid}>
            <div>
              <label style={schedLabel}>Group Name</label>
              <input
                value={rule.group_name || ""}
                onChange={(e) => onChange({ ...rule, group_name: e.target.value })}
                style={smallInput}
                placeholder="DEFAULT"
              />
            </div>
            <div>
              <label style={schedLabel}>Priority <span style={{ color: "#888" }}>(lower = first)</span></label>
              <input
                type="number"
                min="1"
                value={rule.priority ?? ""}
                onChange={(e) => onChange({ ...rule, priority: parseInt(e.target.value) || "" })}
                style={smallInput}
                placeholder="100"
              />
            </div>
            <div>
              <label style={schedLabel}>Max Fires per Group</label>
              <input
                type="number"
                min="1"
                value={rule.group_max_fires ?? ""}
                onChange={(e) => onChange({ ...rule, group_max_fires: parseInt(e.target.value) || "" })}
                style={smallInput}
                placeholder="1"
              />
            </div>
            <div>
              <label style={schedLabel}>Cooldown (minutes)</label>
              <input
                type="number"
                min="0"
                value={rule.cooldown_minutes ?? ""}
                onChange={(e) => onChange({ ...rule, cooldown_minutes: parseInt(e.target.value) || "" })}
                style={smallInput}
                placeholder="0"
              />
            </div>
            <div>
              <label style={schedLabel}>Idempotency TTL (minutes)</label>
              <input
                type="number"
                min="0"
                value={rule.idempotency_ttl_minutes ?? ""}
                onChange={(e) => onChange({ ...rule, idempotency_ttl_minutes: parseInt(e.target.value) || "" })}
                style={smallInput}
                placeholder="60"
              />
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default RuleCard;

/* ---------------- CONDITION BLOCK (recursive — handles leaf and group nodes) ---------------- */

const isGroup = (node) => node && (node.type === "AND" || node.type === "OR");

const ConditionBlock = ({ conditions, factRegistry, onChange, depth = 0 }) => {

  const addCondition = () => {
    onChange([
      ...conditions,
      {
        fact: "",
        operator: ">",
        value_type: "STATIC",
        value: "",
        value_from: "",
        expression: ""
      }
    ]);
  };

  const addGroup = () => {
    onChange([
      ...conditions,
      { type: "OR", children: [] }
    ]);
  };

  const removeAt = (index) => {
    const copy = [...conditions];
    copy.splice(index, 1);
    onChange(copy);
  };

  const updateLeaf = (index, field, value) => {
    const copy = conditions.map((c, i) => i === index ? { ...c, [field]: value } : c);
    if (field === "value_type") {
      copy[index] = { ...copy[index], value: "", value_from: "", expression: "" };
    }
    onChange(copy);
  };

  const updateGroupAt = (index, updatedNode) => {
    onChange(conditions.map((c, i) => i === index ? updatedNode : c));
  };

  const groupedFacts = factRegistry.reduce((acc, fact) => {
    if (!acc[fact.group_name]) acc[fact.group_name] = [];
    acc[fact.group_name].push(fact);
    return acc;
  }, {});

  const containerStyle = depth === 0 ? sectionBox : {
    ...sectionBox,
    background: "#eef2ff",
    border: "1px dashed #7b8cde",
    marginBottom: 0,
  };

  return (
    <div style={containerStyle}>
      {conditions.length === 0 && (
        <div style={emptyStyle}>No conditions added</div>
      )}

      {conditions.map((c, i) => {

        if (isGroup(c)) {
          return (
            <div key={i} style={{ marginBottom: 12 }}>
              {/* Group header: type toggle + remove */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#555" }}>Group:</span>
                <select
                  value={c.type}
                  onChange={(e) => updateGroupAt(i, { ...c, type: e.target.value })}
                  style={{ ...smallInput, width: 80 }}
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>
                <button onClick={() => removeAt(i)} style={{ ...dangerBtn, fontSize: 11, padding: "3px 8px" }}>
                  Remove Group
                </button>
              </div>
              {/* Recursive nested block */}
              <ConditionBlock
                conditions={c.children || []}
                factRegistry={factRegistry}
                onChange={(newChildren) => updateGroupAt(i, { ...c, children: newChildren })}
                depth={depth + 1}
              />
            </div>
          );
        }

        const meta = factRegistry.find(f => f.fact_key === c.fact);

        /* Leaf condition */
        return (
          <div key={i} style={{ marginBottom: 15 }}>
            <div style={rowStyleEditable}>
              <select
                value={c.fact}
                onChange={(e) => updateLeaf(i, "fact", e.target.value)}
                style={smallInput}
              >
                <option value="">Select Fact</option>
                {Object.entries(groupedFacts).map(([group, facts]) => (
                  <optgroup key={group} label={group}>
                    {facts.map(f => (
                      <option key={f.fact_key} value={f.fact_key}>{f.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>

              <select
                value={c.operator}
                onChange={(e) => updateLeaf(i, "operator", e.target.value)}
                style={smallInput}
              >
                {["<", "<=", "==", ">=", ">", "!="].map(op => (
                  <option key={op} value={op}>{op}</option>
                ))}
              </select>

              <select
                value={c.value_type || "STATIC"}
                onChange={(e) => updateLeaf(i, "value_type", e.target.value)}
                style={smallInput}
              >
                <option value="STATIC">Static</option>
                <option value="DYNAMIC">Dynamic</option>
                <option value="EXPRESSION">Expression</option>
              </select>

              <button onClick={() => removeAt(i)} style={dangerBtn}>✕</button>
            </div>

            {c.value_type === "STATIC" && (
              <input
                value={c.value ?? ""}
                onChange={(e) => updateLeaf(i, "value", e.target.value)}
                style={inputStyle}
                placeholder="Enter static value"
              />
            )}

            {c.value_type === "DYNAMIC" && (
              <select
                value={c.value_from || ""}
                onChange={(e) => updateLeaf(i, "value_from", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select Fact</option>
                {Object.entries(groupedFacts).map(([group, facts]) => (
                  <optgroup key={group} label={group}>
                    {facts.map(f => (
                      <option key={f.fact_key} value={f.fact_key}>{f.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}

            {c.value_type === "EXPRESSION" && (
              <input
                value={c.expression || ""}
                onChange={(e) => updateLeaf(i, "expression", e.target.value)}
                style={inputStyle}
                placeholder="Example: strategy.next_buy_price * 1.02"
              />
            )}

            {meta && (
              <GlossaryPanel meta={meta} />
            )}
          </div>
        );
      })}

      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={addCondition} style={secondaryBtn}>+ Add Condition</button>
        {depth === 0 && (
          <button onClick={addGroup} style={{ ...secondaryBtn, borderColor: "#7b8cde", color: "#5060c0" }}>
            + Add OR / AND Group
          </button>
        )}
      </div>
    </div>
  );
};

/* ---------------- ACTION BLOCK ---------------- */

// Actions that accept a min_net_profit parameter configurable from the UI
const MONEY_MGMT_PARAM_ACTIONS = new Set([
  "SQUARE_OFF_MAX_DECAY_POSITION",
  "SQUARE_OFF_MIN_STOCK_MAX_DECAY_POSITION",
  "SQUARE_OFF_FUTURES_MAX_CALL_DECAY_POSITION",
]);

const ActionBlock = ({ actions, actionRegistry, onChange }) => {

  const addAction = () => {
    onChange([
      ...actions,
      { action_key: "", params: {} }
    ]);
  };

  const removeAction = (index) => {
    const copy = [...actions];
    copy.splice(index, 1);
    onChange(copy);
  };

  const updateAction = (index, value) => {
    const copy = [...actions];
    copy[index].action_key = value;
    onChange(copy);
  };

  const updateActionParam = (index, paramKey, value) => {
    const copy = [...actions];
    copy[index] = { ...copy[index], [paramKey]: value };
    onChange(copy);
  };

  const groupedActions = actionRegistry.reduce((acc, act) => {
    if (!acc[act.group_name]) acc[act.group_name] = [];
    acc[act.group_name].push(act);
    return acc;
  }, {});

  return (
    <div style={sectionBox}>
      {actions.length === 0 && (
        <div style={emptyStyle}>No actions added</div>
      )}

      {actions.map((a, i) => {

        const meta = actionRegistry.find(
          x => x.action_key === a.action_key
        );

        return (
          <div key={i} style={{ marginBottom: 15 }}>

            <div style={rowStyleEditable}>

              <select
                value={a.action_key}
                onChange={(e) => updateAction(i, e.target.value)}
                style={smallInput}
              >
                <option value="">Select Action</option>
                {Object.entries(groupedActions).map(([group, acts]) => (
                  <optgroup key={group} label={group}>
                    {acts.map(act => (
                      <option key={act.action_key} value={act.action_key}>
                        {act.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>

              <div></div>
              <div></div>

              <button onClick={() => removeAction(i)} style={dangerBtn}>
                ✕
              </button>
            </div>

            {meta && (
              <GlossaryPanel meta={meta} />
            )}

            {MONEY_MGMT_PARAM_ACTIONS.has(a.action_key) && (
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{ fontSize: 12, color: "#555", whiteSpace: "nowrap" }}>
                  Min Net Profit ($):
                </label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={a.min_net_profit ?? 5000}
                  onChange={(e) =>
                    updateActionParam(i, "min_net_profit", parseFloat(e.target.value) || 0)
                  }
                  style={{ ...smallInput, width: 120 }}
                  placeholder="5000"
                />
              </div>
            )}

          </div>
        );
      })}

      <button onClick={addAction} style={secondaryBtn}>
        + Add Action
      </button>
    </div>
  );
};

const SectionTitle = ({ title }) => (
  <h4 style={sectionTitleStyle}>{title}</h4>
);

/* ─── Action Glossary Panel ────────────────────────────────────────────────
   Shows a short tooltip immediately, then an expandable "What does this
   do to your position?" section with the full description.               */

function GlossaryPanel({ meta }) {
  const [open, setOpen] = useState(false);
  const hasDetail = meta.description && meta.description !== meta.tooltip;

  return (
    <div style={glossaryWrap}>
      {/* Row 1: label + group badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={glossaryLabel}>{meta.label}</span>
        {meta.group_name && (
          <span style={glossaryGroupBadge}>{meta.group_name}</span>
        )}
      </div>

      {/* Row 2: one-line tooltip */}
      {meta.tooltip && (
        <div style={glossaryTooltip}>💡 {meta.tooltip}</div>
      )}

      {/* Row 3: expandable full description */}
      {hasDetail && (
        <>
          <button
            onClick={() => setOpen(o => !o)}
            style={glossaryToggleBtn}
          >
            {open ? "▾ Hide detail" : "▸ What does this do to your position?"}
          </button>
          {open && (
            <div style={glossaryDetail}>
              <div style={glossaryDetailInner}>
                {meta.description}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ---------------- STYLES ---------------- */

const cardStyle = {
  background: "#fff",
  borderRadius: 12,
  marginBottom: 20,
  boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
};

const headerStyle = {
  padding: 16,
  display: "flex",
  alignItems: "center",
  borderBottom: "1px solid #eee"
};

const ruleTitle = {
  fontSize: 16,
  fontWeight: 600
};

const statusBadge = {
  fontSize: 11,
  color: "#fff",
  padding: "3px 8px",
  borderRadius: 12
};

const kebabBtn = {
  padding: "5px 10px",
  borderRadius: 8,
  border: "1px solid #dee2e6",
  cursor: "pointer",
  fontSize: 16,
  fontWeight: 700,
  background: "#fff",
  color: "#555",
  lineHeight: 1,
};

const kebabMenu = {
  position: "absolute", right: 0, top: "calc(100% + 4px)",
  background: "#fff", border: "1px solid #dee2e6",
  borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
  zIndex: 100, minWidth: 130, overflow: "hidden",
};

const kebabMenuItem = {
  display: "block", width: "100%", padding: "10px 16px",
  background: "none", border: "none", cursor: "pointer",
  textAlign: "left", fontSize: 13, color: "#dc3545",
  fontWeight: 600,
};

const sectionTitleStyle = {
  marginTop: 25,
  marginBottom: 15,
  fontSize: 14,
  fontWeight: 600
};

const sectionBox = {
  background: "#f8f9fb",
  padding: 15,
  borderRadius: 8,
  marginBottom: 15
};

const inputStyle = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #ccc",
  fontSize: 13,
  width: "100%",
  marginBottom: 15
};

const rowStyleEditable = {
  display: "grid",
  gridTemplateColumns: "2fr 1fr 1fr 40px",
  gap: 10,
  marginBottom: 10
};

const smallInput = {
  padding: "6px 8px",
  borderRadius: 6,
  border: "1px solid #ccc",
  fontSize: 13,
  width: "100%"
};

const secondaryBtn = {
  padding: "6px 12px",
  borderRadius: 8,
  border: "1px solid #ccc",
  background: "#fff",
  cursor: "pointer"
};

const dangerBtn = {
  background: "#dc3545",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  cursor: "pointer"
};

const emptyStyle = {
  fontSize: 12,
  color: "#888",
  marginBottom: 10
};

const schedGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: 12,
  marginBottom: 15
};

const schedLabel = {
  display: "block",
  fontSize: 11,
  color: "#555",
  marginBottom: 4,
  fontWeight: 500
};

/* ── Plain-English sentence styles ── */
const sentenceBox = {
  display: "flex",
  alignItems: "flex-start",
  gap: 6,
  marginTop: 10,
  padding: "8px 12px",
  background: "#f0f4ff",
  border: "1px solid #d0dcff",
  borderRadius: 8,
  maxWidth: 680,
};
const sentenceIcon = { fontSize: 13, flexShrink: 0, marginTop: 1 };
const sentenceText = {
  fontSize: 13,
  color: "#2c3e6a",
  fontStyle: "italic",
  lineHeight: 1.5,
};
const groupChip = {
  fontSize: 11,
  fontWeight: 600,
  color: "#6f42c1",
  background: "#f0ebff",
  border: "1px solid #d8ccff",
  borderRadius: 10,
  padding: "1px 8px",
};

/* ── Action Glossary styles ── */
const glossaryWrap = {
  marginTop: 8,
  padding: "10px 12px",
  background: "#f8f9fb",
  border: "1px solid #e4e8ef",
  borderRadius: 8,
};
const glossaryLabel = {
  fontSize: 12,
  fontWeight: 700,
  color: "#2c3e50",
};
const glossaryGroupBadge = {
  fontSize: 10,
  fontWeight: 600,
  color: "#fff",
  background: "#6c757d",
  borderRadius: 8,
  padding: "1px 7px",
};
const glossaryTooltip = {
  fontSize: 12,
  color: "#495057",
  lineHeight: 1.5,
  marginBottom: 6,
};
const glossaryToggleBtn = {
  background: "none",
  border: "none",
  color: "#007bff",
  fontSize: 12,
  cursor: "pointer",
  padding: 0,
  fontWeight: 600,
};
const glossaryDetail = {
  marginTop: 8,
  padding: "10px 12px",
  background: "#fff",
  border: "1px solid #d0e8ff",
  borderRadius: 6,
};
const glossaryDetailInner = {
  fontSize: 12,
  color: "#2c3e50",
  lineHeight: 1.7,
};

/* TOGGLE SWITCH */

const ToggleSwitch = ({ checked, onChange }) => (
  <div
    onClick={onChange}
    title={checked ? "Active — click to deactivate" : "Inactive — click to activate"}
    style={{
      width: 52,
      height: 28,
      borderRadius: 14,
      backgroundColor: checked ? "#28a745" : "#ccc",
      position: "relative",
      cursor: "pointer",
      transition: "background-color 0.25s",
      flexShrink: 0,
      boxShadow: "inset 0 1px 3px rgba(0,0,0,0.15)",
    }}
  >
    <div
      style={{
        position: "absolute",
        top: 4,
        left: checked ? 28 : 4,
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
        transition: "left 0.25s",
      }}
    />
  </div>
);